import React from 'react';
import { useQuery } from 'react-query';

import { Modal } from 'antd';
import styled from 'styled-components';

import { getSpeedToLeadTouchpointPreviews } from 'apis/RecommendationsAPI';
import Button from 'components/lib/Button';

import { SPEED_TO_LEAD_FOLLOW_UP_VIEW, SPEED_TO_LEAD_QUERY_KEY } from './constants';
import { getChannelLabel, getEventChannel } from './speedToLeadOverallUtils';
import { TouchpointPreviewSelection } from './SpeedToLeadTouchpointPreviewModal.types';
import SpeedToLeadTouchpointPreviewModalContent from './SpeedToLeadTouchpointPreviewModalContent';

const PreviewModal = styled(Modal)`
  &&& {
    width: min(960px, calc(100vw - 32px)) !important;

    .ant-modal-content {
      display: flex;
      height: 80vh;
      max-height: 80vh;
      flex-direction: column;
      padding: 0;
      overflow: hidden;
      border-radius: 0;
    }

    .ant-modal-header {
      padding: 20px 24px 16px;
      margin: 0;
      border-bottom: 1px solid var(--line-gray);
    }

    .ant-modal-body {
      min-height: 0;
      flex: 1;
      padding: 24px;
      overflow-y: auto;
    }

    .ant-modal-footer {
      padding: 16px 24px;
      margin: 0;
      border-top: 1px solid var(--line-gray);
    }
  }
`;

const ModalHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ModalTitle = styled.div`
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 18px;
`;

const ModalSubtitle = styled.div`
  color: var(--text-secondary);
  font-family: var(--font-regular);
  font-size: 12px;
`;

type Props = {
  analysisRunId: number;
  companyId: number;
  primaryCommunityId: number;
  selection: TouchpointPreviewSelection;
  onClose: () => void;
};

const SpeedToLeadTouchpointPreviewModal = ({
  analysisRunId,
  companyId,
  primaryCommunityId,
  selection,
  onClose,
}: Props) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      SPEED_TO_LEAD_QUERY_KEY.TOUCHPOINT_PREVIEWS,
      analysisRunId,
      companyId,
      primaryCommunityId,
      selection.sourceCommunityId,
    ],
    queryFn: () =>
      getSpeedToLeadTouchpointPreviews(primaryCommunityId, selection.sourceCommunityId, companyId, analysisRunId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const channelEvents = (data?.events ?? []).filter(
    (event) => !selection.channel || getEventChannel(event) === selection.channel
  );
  const titleChannel = selection.channel ? getChannelLabel(selection.channel) : '';
  const title =
    selection.mode === SPEED_TO_LEAD_FOLLOW_UP_VIEW.CHANNEL
      ? `7-Day Follow-Up Persistence - Channel - ${titleChannel} (${channelEvents.length})`
      : '7-Day Follow-Up Persistence - Sequence';

  return (
    <PreviewModal
      open
      classNames={{ body: 'mini-scroll' }}
      title={
        <ModalHeading>
          <ModalTitle>{title}</ModalTitle>
          <ModalSubtitle>
            {selection.communityName} ({selection.isPrimary ? 'My Community' : 'Competitor'})
          </ModalSubtitle>
        </ModalHeading>
      }
      footer={
        <Button onClick={onClose} data-test-id="speed-to-lead-touchpoint-preview-close">
          Close
        </Button>
      }
      onCancel={onClose}
      destroyOnClose
      data-test-id="speed-to-lead-touchpoint-preview-modal"
    >
      <SpeedToLeadTouchpointPreviewModalContent
        data={data}
        isLoading={isLoading}
        isError={isError}
        selection={selection}
      />
    </PreviewModal>
  );
};

export default SpeedToLeadTouchpointPreviewModal;
